import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Cloud,
  CloudRain,
  Thermometer,
  Wind,
  Eye,
  AlertCircle,
} from 'lucide-react';
import { useTripStopWeather, type ForecastDay, type HistoricalMonth } from '@/hooks/useTripStopWeather';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import { useAuth } from '@/contexts/AuthContext';

interface TripStopWeatherProps {
  latitude: number;
  longitude: number;
  arrivalDate: string;
  departureDate: string;
}

function getCloudCoverIcon(cloudCover: number) {
  if (cloudCover < 25) return <Eye className="w-3.5 h-3.5 text-green-500" />;
  if (cloudCover < 50) return <Cloud className="w-3.5 h-3.5 text-yellow-500" />;
  if (cloudCover < 75) return <Cloud className="w-3.5 h-3.5 text-orange-500" />;
  return <Cloud className="w-3.5 h-3.5 text-red-500" />;
}

function getCloudCoverText(cloudCover: number) {
  if (cloudCover < 25) return 'Excellent';
  if (cloudCover < 50) return 'Good';
  if (cloudCover < 75) return 'Fair';
  return 'Poor';
}

function getCloudCoverColor(cloudCover: number) {
  if (cloudCover < 25) return 'text-green-600 bg-green-50 border-green-200';
  if (cloudCover < 50) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  if (cloudCover < 75) return 'text-orange-600 bg-orange-50 border-orange-200';
  return 'text-red-600 bg-red-50 border-red-200';
}

export const TripStopWeather: React.FC<TripStopWeatherProps> = ({
  latitude,
  longitude,
  arrivalDate,
  departureDate,
}) => {
  const { isSubscribed } = useAuth();
  const { data, loading, error, retry } = useTripStopWeather({
    latitude,
    longitude,
    arrivalDate,
    departureDate,
    enabled: isSubscribed,
  });
  const {
    preferredUnits,
    setPreferredUnits,
    formatTemperature,
    formatPrecipitation,
    formatWindSpeed,
  } = useUnitPreferences();

  if (!isSubscribed) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/40 border border-border/50">
        <Skeleton className="h-4 w-full max-w-md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-xs">
        <AlertCircle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
        <span className="text-orange-700">Weather unavailable</span>
        <button onClick={retry} className="text-orange-600 hover:text-orange-800 underline ml-1">
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  if (data.source === 'forecast' && data.daily?.length) {
    return <ForecastView daily={data.daily} formatTemperature={formatTemperature} formatPrecipitation={formatPrecipitation} formatWindSpeed={formatWindSpeed} preferredUnits={preferredUnits} setPreferredUnits={setPreferredUnits} />;
  }

  if (data.source === 'historical' && data.monthly?.length) {
    return <HistoricalView monthly={data.monthly} formatTemperature={formatTemperature} formatPrecipitation={formatPrecipitation} formatWindSpeed={formatWindSpeed} preferredUnits={preferredUnits} setPreferredUnits={setPreferredUnits} />;
  }

  return null;
};

// ---- Forecast: per-day cards ----

function ForecastView({
  daily,
  formatTemperature,
  formatPrecipitation,
  formatWindSpeed,
  preferredUnits,
  setPreferredUnits,
}: {
  daily: ForecastDay[];
  formatTemperature: (c: number) => string;
  formatPrecipitation: (mm: number) => string;
  formatWindSpeed: (kmh: number) => string;
  preferredUnits: string;
  setPreferredUnits: (u: 'imperial' | 'metric') => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
          <Cloud className="w-3.5 h-3.5 text-gold" />
          Weather forecast
        </p>
        <button
          onClick={() => setPreferredUnits(preferredUnits === 'imperial' ? 'metric' : 'imperial')}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
        >
          {preferredUnits === 'imperial' ? '°C' : '°F'}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {daily.map((day) => {
          const dateObj = new Date(day.date + 'T12:00:00');
          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
          const dayNum = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const cloudColor = day.cloud_cover < 25 ? 'border-green-500/30 bg-green-500/5'
            : day.cloud_cover < 50 ? 'border-yellow-500/30 bg-yellow-500/5'
            : day.cloud_cover < 75 ? 'border-orange-500/30 bg-orange-500/5'
            : 'border-red-500/30 bg-red-500/5';
          return (
            <div
              key={day.date}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 ${cloudColor} text-sm`}
            >
              <span className="font-semibold">{dayName}</span>
              <span className="text-xs text-muted-foreground">{dayNum}</span>
              <div className="flex items-center gap-1.5 text-base font-medium">
                {getCloudCoverIcon(day.cloud_cover)}
                <span>{day.cloud_cover}% cloud</span>
              </div>
              <span className="text-muted-foreground">
                {formatTemperature(day.temperature_min, false)}–{formatTemperature(day.temperature_max)}
              </span>
              {day.precipitation > 0 && (
                <span className="flex items-center gap-1 text-blue-500">
                  <CloudRain className="w-3.5 h-3.5" />
                  {formatPrecipitation(day.precipitation)}
                </span>
              )}
              {day.wind_speed_max > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Wind className="w-3 h-3" />
                  {formatWindSpeed(day.wind_speed_max)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Historical: inline summary per month ----

function HistoricalView({
  monthly,
  formatTemperature,
  formatPrecipitation,
  formatWindSpeed,
  preferredUnits,
  setPreferredUnits,
}: {
  monthly: HistoricalMonth[];
  formatTemperature: (c: number) => string;
  formatPrecipitation: (mm: number) => string;
  formatWindSpeed: (kmh: number) => string;
  preferredUnits: string;
  setPreferredUnits: (u: 'imperial' | 'metric') => void;
}) {
  return (
    <div className="space-y-2">
      {monthly.map((m) => (
        <div
          key={m.month}
          className="flex items-center gap-4 flex-wrap px-4 py-3 rounded-lg bg-muted/40 border border-border/50 text-sm text-foreground/80"
        >
          <Cloud className="w-4 h-4 text-gold flex-shrink-0" />
          <span className="text-muted-foreground font-medium">
            {m.monthName} historical avg:
          </span>
          <span className="flex items-center gap-1.5">
            <Thermometer className="w-3.5 h-3.5 text-muted-foreground" />
            {formatTemperature(m.temperature_min, false)}–{formatTemperature(m.temperature_max)}
          </span>
          <span className="text-border">·</span>
          <span className="flex items-center gap-1.5">
            <CloudRain className="w-3.5 h-3.5 text-muted-foreground" />
            {formatPrecipitation(m.precipitation_avg)}
          </span>
          <span className="text-border">·</span>
          <span className="flex items-center gap-1.5">
            <Wind className="w-3.5 h-3.5 text-muted-foreground" />
            {formatWindSpeed(m.wind_speed_avg)}
          </span>
          <span className="text-border">·</span>
          <span className="flex items-center gap-1.5">
            {getCloudCoverIcon(m.cloud_cover_avg)}
            <Badge
              variant="outline"
              className={`text-xs px-1.5 py-0 ${getCloudCoverColor(m.cloud_cover_avg)}`}
            >
              {getCloudCoverText(m.cloud_cover_avg)} clarity
            </Badge>
          </span>
          <button
            onClick={() => setPreferredUnits(preferredUnits === 'imperial' ? 'metric' : 'imperial')}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors underline"
          >
            {preferredUnits === 'imperial' ? '°C' : '°F'}
          </button>
        </div>
      ))}
    </div>
  );
}
